import React from 'react';
import { TrendingUp, TrendingDown, Minus, Flame, Snowflake, Sparkles, ThermometerSun } from 'lucide-react';

/**
 * Real-time buzz meter visualization
 */
export default function BuzzMeter({ buzzData, compact = false }) {
  if (!buzzData) return null;

  const { buzz_score, trend, trend_percentage, sentiment } = buzzData;

  const getHeatIcon = () => {
    if (buzz_score >= 80) return <Flame className="w-6 h-6 text-red-500" />;
    if (buzz_score >= 65) return <ThermometerSun className="w-6 h-6 text-orange-500" />;
    if (buzz_score >= 50) return <Sparkles className="w-6 h-6 text-yellow-500" />;
    return <Snowflake className="w-6 h-6 text-blue-400" />;
  };

  const getHeatColor = () => {
    if (buzz_score >= 80) return 'from-red-500 to-orange-500';
    if (buzz_score >= 65) return 'from-orange-500 to-yellow-500';
    if (buzz_score >= 50) return 'from-yellow-500 to-green-500';
    return 'from-blue-400 to-cyan-400';
  };

  const getTrendIcon = () => {
    if (trend === 'hot' || trend === 'rising') {
      return <TrendingUp className="w-4 h-4 text-green-500" />;
    }
    if (trend === 'declining') {
      return <TrendingDown className="w-4 h-4 text-red-500" />;
    }
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
        {getHeatIcon()}
        <div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-gray-900">{buzz_score}</span>
            <span className="text-sm text-gray-500">/ 100</span>
            {getTrendIcon()}
            <span className={`text-sm ${trend_percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend_percentage >= 0 ? '+' : ''}{trend_percentage}%
            </span>
          </div>
          <p className="text-xs text-gray-500 capitalize">{trend} buzz</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          {getHeatIcon()}
          Event Buzz
        </h3>
        <div className="flex items-center gap-2">
          {getTrendIcon()}
          <span className={`text-sm font-medium ${trend_percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend_percentage >= 0 ? '+' : ''}{trend_percentage}% this week
          </span>
        </div>
      </div>

      {/* Buzz meter */}
      <div className="relative h-8 bg-gray-100 rounded-full overflow-hidden mb-4">
        <div
          className={`absolute inset-y-0 left-0 bg-gradient-to-r ${getHeatColor()} rounded-full transition-all duration-500`}
          style={{ width: `${buzz_score}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-bold text-gray-800 drop-shadow-sm">
            {buzz_score}/100
          </span>
        </div>
      </div>

      {/* Sentiment breakdown */}
      <div className="mb-4">
        <p className="text-sm text-gray-500 mb-2">Sentiment</p>
        <div className="flex h-2 rounded-full overflow-hidden">
          <div
            className="bg-green-500"
            style={{ width: `${sentiment?.positive || 0}%` }}
            title={`Positive: ${sentiment?.positive}%`}
          />
          <div
            className="bg-gray-300"
            style={{ width: `${sentiment?.neutral || 0}%` }}
            title={`Neutral: ${sentiment?.neutral}%`}
          />
          <div
            className="bg-red-400"
            style={{ width: `${sentiment?.negative || 0}%` }}
            title={`Negative: ${sentiment?.negative}%`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{sentiment?.positive}% positive</span>
          <span>{sentiment?.negative}% negative</span>
        </div>
      </div>

      {/* Hot topics */}
      {buzzData.hot_topics && (
        <div>
          <p className="text-sm text-gray-500 mb-2">Trending Topics</p>
          <div className="flex flex-wrap gap-2">
            {buzzData.hot_topics.map((topic, i) => (
              <span
                key={i}
                className="px-2 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}